"use client"

import { memo } from "react"
import { cn, btn3d } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight, Check, Edit3 } from "lucide-react"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"
import { ModeToggle } from "@/components/mode-toggle"
import { QuestionMapContent } from "@/components/dashboard/practice/session/question-map"
import { QuestionHistoryButton } from "@/components/dashboard/practice/session/question-history-button"
import { History } from "lucide-react"

interface SessionFooterProps {
    currentIndex: number;
    total: number;
    questionId?: number;
    onNext: () => void;
    onPrev: () => void;
    onJump: (index: number) => void;
    // onMapClick retired in favor of Popover
    onMapClick?: () => void;
    onCheck?: () => void;
    onFinish: () => void;
    onExit: () => void;
    isChecking?: boolean;
    isCorrect?: boolean;
    getQuestionStatus: (index: number) => { isMastered: boolean; isTried: boolean; isMarked: boolean };
    showQuickFix?: boolean;
    onQuickFix?: () => void;
}

export const SessionFooter = memo(function SessionFooter({
    currentIndex,
    total,
    questionId,
    onNext,
    onPrev,
    onJump,
    onMapClick,
    onCheck,
    onFinish,
    onExit,
    isChecking,
    isCorrect,
    getQuestionStatus,
    showQuickFix,
    onQuickFix
}: SessionFooterProps) {
    const isLastQuestion = currentIndex === total - 1;
    const showFinish = isLastQuestion && (isCorrect !== undefined || !onCheck); // Show finish if last question AND (correct known OR no check button i.e. quiz mode)

    return (
        <footer
            className="h-16 md:h-16 bg-background px-2 sm:px-4 md:px-6 flex items-center justify-between sticky bottom-0 z-10"
            style={{
                backgroundImage:
                    "repeating-linear-gradient(to right, var(--border) 0 12px, transparent 12px 20px)",
                backgroundSize: "100% 1.5px",
                backgroundPosition: "top",
                backgroundRepeat: "no-repeat",
            }}
        >
            <div className="flex items-center gap-2 md:gap-4">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onExit}
                    aria-label="Exit Session"
                    className={cn("h-10 md:h-9 min-w-[48px] md:min-w-[80px] gap-2 rounded-md text-white bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 px-3 md:px-4 relative z-30 font-medium text-base", btn3d)}
                >
                    <span className="hidden md:inline">Exit</span>
                    <span className="md:hidden text-lg">✕</span>
                </Button>

                {questionId && (
                    <QuestionHistoryButton questionId={questionId} minimal />
                )}

                {showQuickFix && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onQuickFix}
                        className={cn("hidden md:inline-flex h-10 md:h-9 min-w-[48px] md:min-w-[80px] px-3 md:px-4 gap-2 rounded-md bg-card border-border text-foreground hover:bg-muted relative font-medium text-sm", btn3d)}
                    >
                        <Edit3 className="h-4 w-4 md:h-5 md:w-5" />
                        <span className="hidden md:inline font-medium text-sm">Quick Fix</span>
                    </Button>
                )}

                <ModeToggle
                    showLabel
                    className={cn("hidden md:inline-flex h-10 md:h-9 min-w-[48px] md:min-w-[80px] px-3 md:px-4 gap-2 rounded-md", btn3d)}
                />
            </div>

            <div className="flex items-center gap-2 md:gap-3 md:absolute md:left-1/2 md:-translate-x-1/2 relative z-20">
                <Popover>
                    <PopoverTrigger asChild>
                        <button
                            aria-label={`Question ${currentIndex + 1} of ${total}`}
                            className={cn("bg-[#2D3039] text-white text-base font-medium px-4 md:px-4 py-2 rounded-md flex items-center justify-center gap-2 md:gap-3 hover:bg-[#3d414d] data-[state=open]:bg-[#3d414d] outline-none min-w-[64px] md:min-w-[140px] h-10 md:h-9", btn3d)}
                        >
                            <span className="hidden sm:inline">Question {currentIndex + 1} of {total}</span>
                            <span className="sm:hidden">{currentIndex + 1}/{total}</span>
                            <span className="text-base opacity-70">▼</span>
                        </button>
                    </PopoverTrigger>
                    <PopoverContent side="top" align="center" className="p-0 border-none bg-transparent shadow-none w-auto">
                        <div className="bg-background rounded-xl shadow-2xl border border-border">
                            <QuestionMapContent
                                total={total}
                                currentIndex={currentIndex}
                                onJump={onJump}
                                getQuestionStatus={getQuestionStatus}
                            // onClose passed via ref if needed, or rely on popover auto-close when clicking button inside (if button isn't preventDefault)
                            // Popover primitives usually don't auto-close on content interaction unless explicitly handled.
                            // We can use PopoverClose or just let it stay open?
                            // Better UX: Close on jump.
                            // To close programmatically nicely without passing Check, we can use a wrapper or the standard accessible close.
                            // For now, passing a no-op or handling it in parent is tricky without state.
                            // Let's adding a generic onClose? PopoverContent from radix-ui often accepts Close.
                            // Actually, simply clicking the button inside often doesn't close it unless we use PopoverClose.
                            />
                        </div>
                    </PopoverContent>
                </Popover>
            </div>

            <div className="flex items-center gap-2 md:gap-3">
                <Button
                    variant="secondary"
                    className={cn("h-10 md:h-9 min-w-[48px] md:min-w-[80px] px-3 md:px-4 gap-1 bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-500/10 dark:text-purple-400 dark:hover:bg-purple-500/20 font-medium text-base", btn3d)}
                    onClick={onPrev}
                    disabled={currentIndex === 0}
                    aria-label="Previous Question"
                >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden md:inline">Back</span>
                </Button>

                {!isLastQuestion && (
                    <Button
                        type="button"
                        className={cn("h-10 md:h-9 min-w-[48px] md:min-w-[80px] px-3 md:px-4 gap-1 bg-[#935BE2] hover:bg-[#8349d1] dark:bg-purple-600 dark:hover:bg-purple-700 font-medium text-base", btn3d)}
                        onClick={onNext}
                        aria-label="Next Question"
                    >
                        <span className="hidden md:inline">Next</span>
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                )}

                {showFinish ? (
                    <Button
                        className={cn("h-10 md:h-9 min-w-[80px] md:min-w-[120px] px-3 md:px-4 bg-green-600 hover:bg-green-700 text-white text-base dark:bg-green-700 dark:hover:bg-green-800 font-medium", btn3d)}
                        onClick={onFinish}
                        disabled={isChecking}
                        aria-label="Finish Session"
                    >
                        <span className="hidden sm:inline">Finish Session</span>
                        <span className="sm:hidden">Finish</span>
                    </Button>
                ) : (
                    onCheck && (
                        <Button
                            className={cn("h-10 md:h-9 min-w-[64px] md:min-w-[100px] px-3 md:px-4 bg-purple-200 text-purple-800 hover:bg-purple-300 text-base md:text-base font-medium dark:bg-purple-500/20 dark:text-purple-300 dark:hover:bg-purple-500/30", btn3d)}
                            onClick={onCheck}
                            disabled={isChecking || isCorrect}
                            aria-label="Check Answer"
                        >
                            <span className="hidden sm:inline">{isChecking ? "Checking..." : "Check"}</span>
                            <span className="sm:hidden">{isChecking ? "..." : "✓"}</span>
                        </Button>
                    )
                )}
            </div>
        </footer>
    )
})

SessionFooter.displayName = "SessionFooter"
